import React, { forwardRef, useRef, useImperativeHandle } from "react";
import { toast } from 'react-toastify';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import ApiClient from "../../../services/api-client"
import ActionsRenderer from '../aggrid-cell-renderers/actions-renderer'
import DoubleValRenderer from "../aggrid-cell-renderers/doubleval-renderer";
import SimpleObjectRenderer from "../aggrid-cell-renderers/simple-object-renderer";
import MyDataMoreTaxesTypeEditor from "../aggrid-cell-editors/myDATA-more-taxes-type-editor";
import MoreTaxesEditor from "../aggrid-cell-editors/more-taxes-editor";
import AmountEditor from "../aggrid-cell-editors/amount-editor";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = {
    myDataMoreTax: { value: undefined, label: undefined },
    type: { value: undefined, label: undefined }
};

const isValid = (data) =>
    data.type && data.type.value != undefined && data.type.value != "" &&
    data.myDataMoreTax && data.myDataMoreTax.value != undefined && data.myDataMoreTax.value != "";

const MoreTaxesGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const toastId = useRef(null);
    const { hasError, setHasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems, deleteRowLocally } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
        globalRows: true,
    });

    const onGridReady = (params) => {
        initGrid(params, props.moreTaxes);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            addRow(params.api);
            params.api.redrawRows();
        }

        const items = [];
        params.api.forEachNode(node => items.push(node));
        items.pop();
        items.forEach(node => {
            if (params.node.data.type && nodeIndex != node.rowIndex) {
                const cond = params.node.data.type.value == node.data.type.value;
                if (cond) {
                    const row_dom = document.querySelector(`.taxesGrid .ag-center-cols-container >  div[row-index="${params.node.rowIndex}"][role="row"]`);
                    row_dom.style.backgroundColor = '#ffb09c';
                    setTimeout(() => {
                        if (!toast.isActive(toastId.current)) {
                            toastId.current = toast.error(t("duplicateMyDataMoreTaxType"), { toastId: "pmt_toastId" });
                        }
                        setHasError(true);
                    }, 100);
                }
                return;
            }
        });

        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        transformData(data, moreTaxesTypesOptions, myDataMoreTaxesOptions) {
            const newData = [];
            data.forEach(el => {
                const cats = [];
                const moreTaxesOptions = [];
                myDataMoreTaxesOptions.forEach(element => {
                    cats.push({
                        value: element.value,
                        label: element.type == "DEDUCTION" ? element.label : t("TAX_" + element.value),
                        type: element.type,
                        key: element.key,
                        uid: element.uid,
                        amountValue: element.amountValue,
                        subtracted: element.subtracted
                    });
                });
                moreTaxesTypesOptions.forEach(element => {
                    moreTaxesOptions.push({ value: element.value, label: t(element.label) });
                });
                const groupTaxes = cats.reduce((r, a) => {
                    r[a.type] = [...r[a.type] || [], a];
                    return r;
                }, {});

                if (el.deduction) {
                    el.myDataMoreTax = {
                        type: "DEDUCTION",
                        id: el.deduction.id,
                        subtracted: el.type == "DEDUCTION" ? el.deduction.subtracted : null,
                    };
                }

                const typeTaxes = groupTaxes[el.myDataMoreTax.type];
                const new_type_options = typeTaxes.map(element => ({
                    value: element.type == "DEDUCTION" ? element.value : "TAX_" + element.value,
                    label: element.type == "DEDUCTION" ? element.label : t("TAX_" + element.value),
                    amountValue: element.amountValue,
                    uid: element.uid,
                    id: element.type == "DEDUCTION" ? element.value : null,
                    subtracted: element.type == "DEDUCTION" ? element.subtracted : null,
                }));

                newData.push({
                    id: el.id,
                    type: { value: el.myDataMoreTax.type, label: t(el.myDataMoreTax.type) },
                    myDataMoreTax: {
                        id: el.deduction ? el.deduction.id : null,
                        uid: el.myDataMoreTax.uid,
                        amountValue: el.myDataMoreTax.amountValue,
                        value: "TAX_" + el.myDataMoreTax.uid,
                        label: el.deduction ? el.deduction.name : t("TAX_" + el.myDataMoreTax.uid),
                        subtracted: el.deduction ? el.deduction.subtracted : null,
                    },
                    myDataMoreTaxesTypes: moreTaxesOptions,
                    myDataMoreTaxes: new_type_options,
                    amount: el.amount,
                });
            });
            return newData;
        },
        getData() {
            return getItems().map(element => {
                element.myDataMoreTax.type = element.type.value;
                return { id: element.id, myDataMoreTax: element.myDataMoreTax, amount: element.amount };
            });
        },
        hasError() {
            return hasError;
        },
        setData(data) {
            setItems(data);
        }
    }));

    const deleteRow = (params) => {
        const rowData = [];
        const id = params.data.id;
        params.api.forEachNode(node => rowData.push(node.data));

        if (id != undefined) {
            ApiClient.deleteProductMoreTaxes(id).then(
                () => {
                    rowData.splice(params.rowIndex, 1);
                    params.api.setRowData(rowData);
                    toast.success("deleteSucceded");
                },
                error => {
                    toast.error("Error: " + error.response.data.message);
                    console.error(error.response);
                }
            );
        } else {
            deleteRowLocally(params);
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="ag-theme-alpine editableSmGrid taxesGrid" style={{ width: '100%' }}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        style={{ width: '100%', height: '300px' }}
                        onCellValueChanged={onCellValueChanged}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        defaultColDef={{
                            flex: 1,
                            resizable: false,
                            sortable: false,
                            wrapText: true,
                            suppressMovable: true,
                        }}
                        suppressRowTransform={true}
                        singleClickEdit={true}
                        skipHeaderOnAutoSize={true}
                        columnDefs={[
                            { field: "myDataMoreTaxes", hide: true },
                            { field: "type", flex: 3, minWidth: 200, headerName: t("myDataMoreTaxesType"), editable: true, cellRendererParams: { moreTaxesOptions: props.moreTaxesOptions, options: props.moreTaxesTypesOptions }, cellEditor: MyDataMoreTaxesTypeEditor, cellRenderer: SimpleObjectRenderer },
                            { field: "myDataMoreTax", flex: 3, minWidth: 200, headerName: t("tax"), editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: MoreTaxesEditor },
                            { field: "amount", flex: 1, minWidth: 100, headerName: t("Amount"), editable: true, cellRenderer: DoubleValRenderer, cellEditor: AmountEditor },
                            { field: "name", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRow: deleteRow } }
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
            <div><br /></div>
        </div>
    );
});
export default MoreTaxesGrid;
